
import { Component, Vue, Watch } from "vue-property-decorator";
import DimssaButton, { ButtonState } from "@/components/shared/dimssa-button.vue";
import { listCustomFeeder, listIntakeBatch } from "@/helpers/graphql-list-items";

@Component({
  components: {
    DimssaButton,
  },
})
export default class IntakeBatchChangeOwner extends Vue {
  tableHeight: number = 350;

  getIntakeBatchesButtonState: ButtonState = "ready";
  getOwnersButtonState: ButtonState = "ready";
  changeOwnerButtonState: ButtonState = "disabled";
  getHistoryButtonState: ButtonState = "ready";

  batchSearch: string = "";
  ownerSearch: string = "";

  selectedBatches: any[] = [];
  selectedOwners: any[] = [];

  batchItems: any[] = [];
  ownerItems: any[] = [];
  changeOwnerResponseMessages: string[] = [];
  historyMessages: string[] = [];

  batchHeaders = [
    { text: "Reference", value: "reference" },
    { text: "Owner", value: "ownerName" },
    { text: "Quantities", value: "quantities", sortable: false },
  ];

  ownerHeaders = [{ text: "Name", value: "name" }];

  get selectedBatchReference() {
    return this.selectedBatches[0]?.reference ?? "";
  }

  get selectedOwnerName() {
    return this.selectedOwners[0]?.name ?? "";
  }

  get selectedBatchGuid() {
    return this.selectedBatches[0]?.guid ?? "";
  }

  get selectedOwnerGuid() {
    return this.selectedOwners[0]?.guid ?? "";
  }

  @Watch("selectedBatches")
  onSelectedBatchesChanged() {
    this.updateChangeOwnerButtonState();
  }

  @Watch("selectedOwners")
  onSelectedOwnersChanged() {
    this.updateChangeOwnerButtonState();
  }

  created() {
    if (this.$store.state.user.user?.email !== "dolan@gigalot.co.za") {
      this.$router.push({ name: "home" });
      return;
    }

    this.$store.commit("navFuncs", {
      save: undefined,
      back: () => {
        this.$router.push({ name: "home" });
      },
    });

    this.$nextTick(this.updateTableHeight);
    window.addEventListener("resize", this.updateTableHeight);
  }

  destroyed() {
    window.removeEventListener("resize", this.updateTableHeight);
  }

  updateTableHeight = () => {
    const batchTable = document.getElementById("intake-batch-change-owner-batches-table");
    if (!batchTable) return;

    const rect = batchTable.getBoundingClientRect();
    const available = Math.floor((window.innerHeight - rect.top - 140) / 2);
    this.tableHeight = Math.max(200, available);
  };

  intakeQuantityRows(item: any) {
    const rows: string[] = [];
    if (this.hasValidQuantity(item.quantityExpected)) rows.push(`${item.quantityExpected} Expected`);
    if (this.hasValidQuantity(item.quantityProcessed)) rows.push(`${item.quantityProcessed} Processed`);
    if (this.hasValidQuantity(item.quantityDead)) rows.push(`${item.quantityDead} Dead`);
    return rows;
  }

  hasValidQuantity(value: any) {
    return value !== null && value !== undefined && value !== "";
  }

  updateChangeOwnerButtonState() {
    if (this.changeOwnerButtonState === "busy") return;
    this.changeOwnerButtonState = this.selectedBatchGuid && this.selectedOwnerGuid ? "ready" : "disabled";
  }

  async getIntakeBatches() {
    try {
      this.getIntakeBatchesButtonState = "busy";
      const result = await listIntakeBatch(true, {
        page: 1,
        itemsPerPage: 1000,
        sortBy: ["timeCreated"],
        sortDesc: [true],
        search: "",
        timeFrom: this.$store.state.moment().subtract(12, "months").valueOf(),
        timeUntil: this.$store.state.moment().valueOf(),
      });
      this.batchItems = result.items;
      this.selectedBatches = [];
      this.getIntakeBatchesButtonState = "success";
      this.updateChangeOwnerButtonState();
      this.$nextTick(this.updateTableHeight);
    } catch (err) {
      console.error(err);
      this.getIntakeBatchesButtonState = "error";
    }
  }

  async getOwners() {
    try {
      this.getOwnersButtonState = "busy";
      this.ownerItems = await listCustomFeeder(true);
      this.selectedOwners = [];
      this.getOwnersButtonState = "success";
      this.updateChangeOwnerButtonState();
      this.$nextTick(this.updateTableHeight);
    } catch (err) {
      console.error(err);
      this.getOwnersButtonState = "error";
    }
  }

  async changeOwner() {
    if (!this.selectedBatchGuid) {
      this.$store.commit("popup/displayOk", ["Select an intake batch first."]);
      return;
    }

    if (!this.selectedOwnerGuid) {
      this.$store.commit("popup/displayOk", ["Select an owner first."]);
      return;
    }

    this.$store.commit("popup/displayYesNo", {
      messages: [`Change owner of batch ${this.selectedBatchReference}?`, `New owner: ${this.selectedOwnerName}`],
      yesAction: async () => {
        try {
          this.changeOwnerButtonState = "busy";
          this.$store.commit("popup/displayWait", ["Changing owner..."]);

          const json = await this.$store.dispatch(
            "graphQl",
            {
              gql: `mutation ChangeOwnersOfIntakeBatch($guid: String!, $batchGuid: String!, $customFeederGuid: String!, $userEmail: String!) {
                changeOwnersOfIntakeBatch(
                  guid: $guid
                  batchGuid: $batchGuid
                  customFeederGuid: $customFeederGuid
                  userEmail: $userEmail
                ) {
                  success
                  batchGuid
                  batchReference
                  oldCustomFeederGuid
                  oldCustomFeederName
                  newCustomFeederGuid
                  newCustomFeederName
                  numAnimalsUpdated
                  numProcessingResultsFound
                  numProcessedAnimalsFound
                  animalSgtins
                }
              }`,
              variables: {
                guid: this.$store.state.user.location.guid,
                batchGuid: this.selectedBatchGuid,
                customFeederGuid: this.selectedOwnerGuid,
                userEmail: this.$store.state.user.user?.email ?? "",
              },
              timeout: 10 * 1000,
            },
            { root: true }
          );

          const result = json?.data?.changeOwnersOfIntakeBatch;
          if (!result) {
            throw Error(`No result returned: ${JSON.stringify(json)}`);
          }
          if (!result.success) {
            throw Error(`Owner change failed: ${JSON.stringify(result)}`);
          }

          this.changeOwnerResponseMessages = [
            `Owner changed for batch ${result.batchReference || this.selectedBatchReference}.`,
            `Old Owner: ${result.oldCustomFeederName || ""}`,
            `New Owner: ${result.newCustomFeederName || ""}`,
            `Animals Updated: ${result.numAnimalsUpdated}`,
            `Processing Results Found: ${result.numProcessingResultsFound}`,
            `Processed Animals Found: ${result.numProcessedAnimalsFound}`,
            `Animal Sgtins: ${Array.isArray(result.animalSgtins) ? result.animalSgtins.join(", ") : ""}`,
          ];

          this.$store.commit("popup/hide");
          this.changeOwnerButtonState = "success";
          this.$store.commit("popup/displayOk", {
            messages: this.changeOwnerResponseMessages,
          });
        } catch (err) {
          console.error(err);
          this.$store.commit("popup/hide");
          this.changeOwnerButtonState = "error";
          this.$store.commit("popup/displayOk", { messages: [`Error: ${err}`] });
        } finally {
          this.updateChangeOwnerButtonState();
        }
      },
    });
  }

  async getHistory() {
    try {
      this.getHistoryButtonState = "busy";

      const json = await this.$store.dispatch(
        "graphQl",
        {
          gql: `query ChangeOwnersOfIntakeBatchHistory($guid: String!) {
            changeOwnersOfIntakeBatchHistory(guid: $guid)
          }`,
          variables: {
            guid: this.$store.state.user.location.guid,
          },
          timeout: 10 * 1000,
        },
        { root: true }
      );

      this.historyMessages = json?.data?.changeOwnersOfIntakeBatchHistory ?? [];
      this.getHistoryButtonState = "success";
    } catch (err) {
      console.error(err);
      this.getHistoryButtonState = "error";
      this.$store.commit("popup/displayOk", { messages: [`Error: ${err}`] });
    }
  }
}
